import { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  clearBootstrapThemeFailure,
  getThemeKeyFromModulePath,
  hasBootstrapThemeFailed,
  PARTNER_THEME_SSR_FLAG,
  PREWARM_TIMEOUT_MS,
} from './prewarm-client-utils';
import { abandonInFlightLoad, getLoadedTheme, getThemeCacheKey, loadTheme } from './theme-cache';

export type RegistryThemeStatus = 'failed' | 'idle' | 'loading' | 'ready';

type RegistryTheme = {
  status: RegistryThemeStatus;
  theme: ReturnType<typeof getLoadedTheme>;
};

// Owns the whole partner-theme registry lifecycle for the current theme key. `failed` is latched
// until the key changes, so a late cache write can never swap a mounted federated theme out
// mid-visit, and returning to a previously failed key starts a fresh attempt.
export function useRegistryTheme({
  hasLivePreviewManifest,
  modulePath,
  scope,
}: {
  hasLivePreviewManifest: boolean;
  modulePath: string | null | undefined;
  scope: string | null | undefined;
}): RegistryTheme {
  const [, setCacheRevision] = useState(0);
  // useFlags() is seeded from the same ldBootstrap that getServerSideProps reads, on the server and
  // on the first client render, so both agree on this flag and hydration cannot diverge on it.
  const flags = useFlags();
  const isEligible = flags[PARTNER_THEME_SSR_FLAG] === true && !hasLivePreviewManifest;
  const themeKey = getThemeKeyFromModulePath(modulePath);
  const loadKey = isEligible && scope && themeKey ? getThemeCacheKey(scope, themeKey) : null;

  // Seeded once per mount from the browser bootstrap result: if SSR prewarmed a theme but the
  // bootstrap failed, render #1 holds slots blank (known React 18 hydration mismatch) and the
  // client falls straight back to Module Federation instead of racing the registry a second time.
  const [failedKey, setFailedKey] = useState<string | null>(() =>
    loadKey && scope && themeKey && hasBootstrapThemeFailed(scope, themeKey) ? loadKey : null
  );
  const [trackedKey, setTrackedKey] = useState(loadKey);
  if (trackedKey !== loadKey) {
    setTrackedKey(loadKey);
    setFailedKey(null);
  }

  const hasFailed = loadKey !== null && failedKey === loadKey;
  const loadedTheme = loadKey && scope && themeKey && !hasFailed ? getLoadedTheme(scope, themeKey) : undefined;

  let status: RegistryThemeStatus = 'idle';
  if (loadKey) {
    if (hasFailed) status = 'failed';
    else if (loadedTheme) status = 'ready';
    else status = 'loading';
  }

  useEffect(() => {
    if (!loadKey || !scope || !themeKey || status === 'ready') {
      return undefined;
    }
    // The singleton is one-shot: consume it here (not during render) so React 18 StrictMode
    // re-runs still see the failure latched in `failedKey`.
    if (hasBootstrapThemeFailed(scope, themeKey)) {
      clearBootstrapThemeFailure(scope, themeKey);
      setFailedKey(loadKey);
      return undefined;
    }
    if (status === 'failed') {
      return undefined;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const fail = (): void => setFailedKey(loadKey);

    const loadPromise = loadTheme(scope, themeKey);
    const timeoutPromise = new Promise<'timeout'>((resolve) => {
      timeoutId = setTimeout(() => resolve('timeout'), PREWARM_TIMEOUT_MS);
    });

    Promise.race([loadPromise.catch(() => null), timeoutPromise])
      .then((result) => {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        if (cancelled) return;
        if (result === 'timeout') {
          abandonInFlightLoad(scope, themeKey, loadPromise);
          fail();
        } else if (result) {
          setCacheRevision((revision) => revision + 1);
        } else {
          fail();
        }
      })
      .catch(() => {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        if (!cancelled) fail();
      });

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [loadKey, scope, themeKey, status]);

  return { status, theme: loadedTheme };
}
